import React from "react";
import { FormattedMessage } from "react-intl";

import { useTranslateStatus } from "@/api/statuses";
import { updateStatusCache } from "@/api/statuses/utils";
import { HStack, Text } from "@/components/ui";
import { useLocale } from "@/hooks";
import { type Status } from "@/schemas";
import { cn } from "@/utils/cn";
import { unescapeHTML } from "@/utils/html";
import { getLanguageName } from "@/utils/languages";

interface TranslationButtonProps {
  status: Status;
}

export default function TranslationButton({ status }: TranslationButtonProps) {
  const { locale } = useLocale();
  const { mutate: translateStatus, isLoading: isTranslationLoading } =
    useTranslateStatus();

  const isTranslatable = locale !== status.language;

  if (!isTranslatable) {
    return null;
  }

  const handleClick: React.MouseEventHandler = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (status.translatedFrom) {
      // reset to original content
      updateStatusCache(status.id, () => ({
        translatedContent: "",
        translatedTitle: "",
        translatedFrom: "",
      }));
    } else {
      translateStatus({
        id: status.id,
        text: unescapeHTML(status.content),
        title: status.title ? unescapeHTML(status.title) : undefined,
        from_lang: status.language || "en",
        to_lang: locale,
      });
    }
  };

  const renderText = () => {
    if (isTranslationLoading) {
      return (
        <FormattedMessage
          id="status.content.translating"
          defaultMessage="Translating..."
        />
      );
    }

    if (status.translatedFrom) {
      return (
        <FormattedMessage
          id="status.content.show_original"
          defaultMessage="Show original"
        />
      );
    }

    return (
      <FormattedMessage
        id="status.content.show_translation"
        defaultMessage="Show translation"
      />
    );
  };

  return (
    <HStack space={1}>
      {status.translatedFrom && (
        <Text size="sm" theme="muted">
          <FormattedMessage
            id="status.content.translated_from"
            defaultMessage="Translated from {lang}"
            values={{ lang: getLanguageName(status.translatedFrom) }}
          />
        </Text>
      )}

      <button
        onClick={handleClick}
        type="button"
        className="w-fit"
        disabled={isTranslationLoading}
      >
        <Text
          theme="primary"
          size="sm"
          weight="medium"
          className={cn({
            "!dark:text-gray-400 text-gray-600!": isTranslationLoading,
          })}
        >
          {renderText()}
        </Text>
      </button>
    </HStack>
  );
}
